/**
 * MergeActionItemsModal Component
 * Modal for merging multiple action items into a single target item.
 * Allows user to select which item should be the merge target.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether the modal is visible
 * @param {Function} props.onClose - Function to close the modal
 * @param {Set} props.selectedItems - Set of selected item IDs to merge
 * @param {number} props.mergeTargetId - ID of the target item to merge into
 * @param {Function} props.onMergeTargetChange - Function to handle merge target selection
 * @param {Function} props.onMerge - Function to execute the merge
 * @param {boolean} props.isMerging - Whether merge operation is in progress
 * @param {Array} props.actionItems - Array of all action items for lookup
 */
const MergeActionItemsModal = ({
    show,
    onClose,
    selectedItems,
    mergeTargetId,
    onMergeTargetChange,
    onMerge,
    isMerging,
    actionItems
}) => {
    if (!show) return null;
    
    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-content merge-modal" onClick={(e) => e.stopPropagation()}>
                <h2>Merge Action Items</h2>
                
                <div className="merge-info">
                    <p>You are about to merge <strong>{selectedItems.size}</strong> action items into one.</p>
                    <p>Select which item should be the target (other items will be closed and linked to it):</p>
                </div>
                
                <div className="merge-target-selection">
                    <label>Target Action Item:</label>
                    <select 
                        value={mergeTargetId || ''} 
                        onChange={onMergeTargetChange}
                    >
                        {Array.from(selectedItems).map(itemId => {
                            const item = actionItems.find(i => i.id === itemId);
                            return (
                                <option key={itemId} value={itemId}>
                                    #{itemId} - {item?.title || item?.description || item?.nonCompliance || 'No description'}
                                </option>
                            );
                        })}
                    </select>
                </div>
                
                <div className="merge-preview">
                    <h4>Items to be merged:</h4>
                    <ul>
                        {Array.from(selectedItems).filter(id => id !== mergeTargetId).map(itemId => {
                            const item = actionItems.find(i => i.id === itemId);
                            return (
                                <li key={itemId}>
                                    <strong>#{itemId}</strong> - {item?.title || item?.description || item?.nonCompliance || 'No description'}
                                    <span className="merge-action">→ Will be closed</span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
                
                <div className="modal-actions">
                    <button 
                        type="button"
                        onClick={onClose}
                        disabled={isMerging}
                    >
                        Cancel
                    </button>
                    <button 
                        type="button"
                        className="btn-merge-confirm"
                        onClick={onMerge}
                        disabled={isMerging || !mergeTargetId}
                    >
                        {isMerging ? 'Merging...' : 'Confirm Merge'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MergeActionItemsModal;
